/**
 * A company's PURCHASE HISTORY — the products it bought and the projects it
 * took part in, broken down by cycle. One copy of the wording and the folding,
 * shared by the list cell and the record page.
 *
 * ⛔ Nothing here fetches or writes. It decides what the screen may say given
 * what the server sent.
 *
 * Three states, kept apart:
 * - `null` — the server did not say (an older API that omits the key).
 * - `[]` — the server looked and the company bought nothing.
 * - a non-empty array — cycles, newest first, the `cycle: null` bucket last.
 *
 * ⛔ Never `?? []` between the wire and the screen: that turns *not reported*
 * into *bought nothing*.
 */
import type { CompanyPurchaseCycle, CompanyPurchaseRef } from '@/lib/types';

export const PRODUCTS_PURCHASED_LABEL = 'Products purchased';
export const PROJECTS_PARTICIPATED_LABEL = 'Participating Projects';
/**
 * The record page's section heading. Projects only since 2026-10-12 — the owner
 * removed products from the company page; the list cells still show both.
 */
export const PURCHASE_HISTORY_HEADING = 'Participating Projects';
export const NO_CYCLE_LABEL = 'No cycle';

export type PurchaseKind = 'products' | 'projects';

/** Folds an absent key (older server) to `null`, once, here. */
export function purchaseHistoryOf(company: {
  purchaseHistory?: CompanyPurchaseCycle[] | null;
}): readonly CompanyPurchaseCycle[] | null {
  return company.purchaseHistory ?? null;
}

/** The cycle's name, or "No cycle" for the bucket with no cycle. */
export function purchaseCycleLabel(cycle: CompanyPurchaseCycle['cycle']): string {
  if (cycle === null) return NO_CYCLE_LABEL;
  const trimmed = cycle.name.trim();
  return trimmed === '' ? `#${cycle.id}` : trimmed;
}

/**
 * Every product (or project) across all cycles, distinct by id, in first
 * appearance order — so newest cycle first. `null` in gives `null` out.
 */
export function purchasedItemsOf(
  history: readonly CompanyPurchaseCycle[] | null,
  kind: PurchaseKind,
): CompanyPurchaseRef[] | null {
  if (history === null) return null;
  const seen = new Set<number>();
  const out: CompanyPurchaseRef[] = [];
  for (const bucket of history) {
    for (const item of bucket[kind]) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(item);
    }
  }
  return out;
}

/**
 * One line per cycle that has items — `2026 - 27: A, B`. `undefined` when the
 * history was not reported or holds nothing of this kind.
 */
export function purchaseTitle(
  history: readonly CompanyPurchaseCycle[] | null,
  kind: PurchaseKind,
): string | undefined {
  if (history === null) return undefined;
  const lines: string[] = [];
  for (const bucket of history) {
    const items = bucket[kind];
    if (items.length === 0) continue;
    const names = items.map((item) => {
      const trimmed = item.name.trim();
      return trimmed === '' ? `#${item.id}` : trimmed;
    });
    lines.push(`${purchaseCycleLabel(bucket.cycle)}: ${names.join(', ')}`);
  }
  return lines.length === 0 ? undefined : lines.join('\n');
}
